import { getFormattedCouponReduction } from "@/modules/common/couponObject/utils/couponObjectUtils"
import { BoxOfferDetailedEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { WARNING_OFFER_END_DELAY } from "@modules/boxSubscription/entities/boxSubscriptionConst"
import {
  type CouponInputFormik,
  DurationType,
  type PauseInputFormik,
} from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import {
  type BoxSubscriptionDetailsDto,
  BoxSubscriptionStatus,
} from "@modules/boxSubscription/gateway/boxSubscriptionApiDto"
import { CouponEntity } from "@modules/common/couponObject/entities/couponObjectEntity"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { PauseEntity } from "@modules/common/pauseObject/entities/pauseObjectEntity"
import { PriceObjectEntity } from "@modules/common/priceObject/entities/priceObjectEntity"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { type PaymentError } from "@modules/finances/types/stripeErrorTypes"
import { Language } from "@modules/language/utils/languageUtils"
import { LightPromoCodeEntity } from "@modules/promoCode/entities/lightPromoCodeEntity"
import dayjs from "dayjs"

type BoxSubscriptionDetailsState = Omit<
  BoxSubscriptionDetailsEntity,
  | "getWordingStatus"
  | "getColorStatus"
  | "getWordingCouponDate"
  | "getAppliedPromocodeWording"
  | "getAppliedCouponWording"
  | "isDisplayWarning"
  | "getWordingWarning"
  | "getCoupon"
  | "getPause"
  | "getWordingPause"
  | "getCurrentPrice"
>

export class BoxSubscriptionDetailsEntity {
  id: string
  type: BoxOfferType
  creationDate: Date
  status: BoxSubscriptionStatus
  periodEndDate: Date | null
  minimumCommitmentDate: Date | null
  minimumCommitmentPeriod: string | null
  errors: PaymentError | null
  pause: PauseEntity | null
  coupon: CouponEntity | null
  referencePrice: PriceObjectEntity | null
  priceObject: PriceObjectEntity | null
  subscriptionDate: Date
  subscriptionEndDate: Date | null
  recurringPeriod: string | null
  deferredDate: Date | null
  usedPromoCode: LightPromoCodeEntity | null
  offer: BoxOfferDetailedEntity
  outOfBand: boolean

  private constructor(boxSubcription: BoxSubscriptionDetailsState) {
    this.id = boxSubcription.id
    this.type = boxSubcription.type
    this.creationDate = boxSubcription.creationDate
    this.status = boxSubcription.status
    this.periodEndDate = boxSubcription.periodEndDate
    this.minimumCommitmentDate = boxSubcription.minimumCommitmentDate
    this.minimumCommitmentPeriod = boxSubcription.minimumCommitmentPeriod
    this.errors = boxSubcription.errors
    this.pause = boxSubcription.pause
    this.coupon = boxSubcription.coupon
    this.referencePrice = boxSubcription.referencePrice
    this.priceObject = boxSubcription.priceObject
    this.subscriptionDate = boxSubcription.subscriptionDate
    this.subscriptionEndDate = boxSubcription.subscriptionEndDate
    this.recurringPeriod = boxSubcription.recurringPeriod
    this.deferredDate = boxSubcription.deferredDate
    this.usedPromoCode = boxSubcription.usedPromoCode
    this.offer = boxSubcription.offer
    this.outOfBand = boxSubcription.outOfBand
  }

  static fromDto(boxSubcriptionDto: BoxSubscriptionDetailsDto): BoxSubscriptionDetailsEntity {
    return new BoxSubscriptionDetailsEntity({
      id: boxSubcriptionDto.id,
      type: boxSubcriptionDto.type,
      creationDate: dayjs(boxSubcriptionDto.creationDate).toDate(),
      status: boxSubcriptionDto.status,
      periodEndDate: boxSubcriptionDto.periodEndDate ? dayjs(boxSubcriptionDto.periodEndDate).toDate() : null,
      minimumCommitmentDate: boxSubcriptionDto.minimumCommitmentDate
        ? dayjs(boxSubcriptionDto.minimumCommitmentDate).toDate()
        : null,
      minimumCommitmentPeriod: boxSubcriptionDto.minimumCommitmentPeriod,
      errors: boxSubcriptionDto.errors,
      pause: boxSubcriptionDto.pause ? PauseEntity.fromDto(boxSubcriptionDto.pause) : null,
      coupon: boxSubcriptionDto.coupon ? CouponEntity.fromDto(boxSubcriptionDto.coupon) : null,
      referencePrice: boxSubcriptionDto.referencePrice
        ? PriceObjectEntity.fromDto(boxSubcriptionDto.referencePrice)
        : null,
      priceObject: boxSubcriptionDto.priceObject ? PriceObjectEntity.fromDto(boxSubcriptionDto.priceObject) : null,
      subscriptionDate: dayjs(boxSubcriptionDto.subscriptionDate).toDate(),
      subscriptionEndDate: boxSubcriptionDto.subscriptionEndDate
        ? dayjs(boxSubcriptionDto.subscriptionEndDate).toDate()
        : null,
      deferredDate: boxSubcriptionDto.deferredDate ? dayjs(boxSubcriptionDto.deferredDate).toDate() : null,
      recurringPeriod: boxSubcriptionDto.recurringPeriod,
      usedPromoCode: boxSubcriptionDto.usedPromoCode
        ? LightPromoCodeEntity.fromDto(boxSubcriptionDto.usedPromoCode)
        : null,
      offer: BoxOfferDetailedEntity.fromDto(boxSubcriptionDto.offer),
      outOfBand: boxSubcriptionDto.outOfBand,
    })
  }

  getCoupon(): CouponInputFormik {
    if (!this.coupon) {
      return {
        type: CouponType.PERCENT,
        value: 0,
        durationType: DurationType.UNLIMITED,
        months: 0,
      }
    }
    return {
      type: this.coupon.type,
      value: this.coupon.value,
      durationType: this.coupon.duration ? DurationType.MONTHLY : DurationType.UNLIMITED,
      months: this.coupon.duration || null,
    }
  }

  getPause(): PauseInputFormik {
    if (!this.pause) {
      return {
        durationType: DurationType.MONTHLY,
        months: 1,
      }
    }

    if (this.pause.resumeAt === null && this.pause.pausedAt) {
      return {
        durationType: DurationType.UNLIMITED,
        months: 0,
      }
    }

    const pausedAt = dayjs(this.pause.pausedAt)
    const resumeAt = dayjs(this.pause.resumeAt)
    const months = resumeAt.diff(pausedAt, "month")

    return {
      durationType: months > 0 ? DurationType.MONTHLY : DurationType.UNLIMITED,
      months: months > 0 ? months : 1,
    }
  }

  getWordingStatus(): string {
    switch (this.status) {
      case BoxSubscriptionStatus.ACTIVE:
      case BoxSubscriptionStatus.NEW_BILLING_DATE:
        return word("box.members.tab.offer.status.ongoing")
      case BoxSubscriptionStatus.PENDING_PAYMENT:
        return word("box.members.tab.offer.status.pendingPayment") // Should not be used yet as this status means that the athlete is a lead
      case BoxSubscriptionStatus.PAUSE:
        return word("box.members.tab.offer.status.suspended")
      case BoxSubscriptionStatus.INACTIVE:
        return word("box.members.tab.offer.status.expired")
      case BoxSubscriptionStatus.PAYMENT_IN_ERROR:
        return word("box.members.tab.offer.status.paymentError")
      case BoxSubscriptionStatus.PENDING_CANCELLATION:
        return word("box.members.tab.offer.status.pendingCancellation")
      case BoxSubscriptionStatus.SCHEDULED_ACTIVATION:
        return word("box.members.tab.offer.status.delayedStart")
      case BoxSubscriptionStatus.PROCESSING_PAYMENT:
        return word("box.members.tab.offer.status.processingPayment")
      default:
        return ""
    }
  }

  getColorStatus(): string {
    switch (this.status) {
      case BoxSubscriptionStatus.ACTIVE:
      case BoxSubscriptionStatus.NEW_BILLING_DATE:
      case BoxSubscriptionStatus.SCHEDULED_ACTIVATION:
      case BoxSubscriptionStatus.PROCESSING_PAYMENT:
        return "text-textSuccess"
      case BoxSubscriptionStatus.PENDING_PAYMENT:
      case BoxSubscriptionStatus.PAUSE:
        return "text-custom-yellow-500"
      case BoxSubscriptionStatus.INACTIVE:
      case BoxSubscriptionStatus.PAYMENT_IN_ERROR:
      case BoxSubscriptionStatus.PENDING_CANCELLATION:
        return "text-red-500"
      default:
        return "text-red-500"
    }
  }

  getWordingCouponDate(): string {
    if (!this.coupon) return ""

    return this.coupon.getWordingDate()
  }

  getAppliedPromocodeWording(language: Language): string {
    if (!this.referencePrice || !this.usedPromoCode) {
      return ""
    }

    const initialPrice = this.referencePrice.value
    const reduction =
      this.usedPromoCode.type === CouponType.PERCENT
        ? (initialPrice * this.usedPromoCode.value) / 100
        : this.usedPromoCode.value
    const resultingPrice = initialPrice - reduction
    const formattedReduction = getFormattedCouponReduction(
      this.usedPromoCode.type,
      this.usedPromoCode.value,
      language,
      this.referencePrice.currency,
    )

    if (this.usedPromoCode.type === CouponType.PERCENT) {
      return (
        `${word("promoCode.short", { code: this.usedPromoCode.code })}: ` +
        `${word("coupon.subscription.reduction.definition", {
          percent: formattedReduction,
          price: formatCurrency(initialPrice, this.referencePrice.currency, language),
        })} ` +
        `= -${formatCurrency(reduction, this.referencePrice.currency, language)} ` +
        `${word("coupon.subscription.reduction.result", {
          result: formatCurrency(resultingPrice, this.referencePrice.currency, language),
        })}`
      )
    } else {
      return (
        `${word("promoCode.short", { code: this.usedPromoCode.code })}: ` +
        `-${formattedReduction}, ${word("coupon.subscription.reduction.result", {
          result: formatCurrency(resultingPrice, this.referencePrice.currency, language),
        })}`
      )
    }
  }

  // The coupon is calculated after calculating the promo code if there is one
  getAppliedCouponWording(language: Language): string {
    if (!this.priceObject || !this.referencePrice || !this.coupon) {
      return ""
    }

    let initialPrice = this.referencePrice.value
    if (this.usedPromoCode !== null) {
      initialPrice =
        this.usedPromoCode.type === CouponType.PERCENT
          ? initialPrice - (initialPrice * this.usedPromoCode.value) / 100
          : initialPrice - this.usedPromoCode.value
    }

    const reduction =
      this.coupon.type === CouponType.PERCENT ? (initialPrice * this.coupon.value) / 100 : this.coupon.value
    const resultingPrice = initialPrice - reduction

    const formattedReduction = getFormattedCouponReduction(
      this.coupon.type,
      this.coupon.value,
      language,
      this.priceObject.currency,
    )

    if (this.coupon.type === CouponType.PERCENT) {
      return (
        `${word("coupon.subscription.reduction.definition", {
          percent: formattedReduction,
          price: formatCurrency(initialPrice, this.referencePrice.currency, language),
        })} ` +
        `= -${formatCurrency(reduction, this.priceObject.currency, language)} ` +
        ` ${word("coupon.subscription.reduction.result", { result: formatCurrency(resultingPrice, this.priceObject.currency, language) })}`
      )
    } else {
      return `-${formattedReduction}, ${word("coupon.subscription.reduction.result", {
        result: formatCurrency(resultingPrice, this.priceObject.currency, language),
      })}`
    }
  }

  isDisplayWarning(): boolean {
    if (
      this.status === BoxSubscriptionStatus.PENDING_CANCELLATION &&
      this.subscriptionEndDate !== null &&
      dayjs(this.subscriptionEndDate).diff(dayjs(), "day") <= WARNING_OFFER_END_DELAY
    )
      return true

    return false
  }

  getWordingWarning(): string {
    if (this.subscriptionEndDate === null) return ""

    return word("box.member.table.offer.item.status.ends", {
      count: dayjs(this.subscriptionEndDate).startOf("day").diff(dayjs().startOf("day"), "day"),
    })
  }

  getWordingPause(): string {
    if (!this.pause) return ""
    if (!this.pause.resumeAt) {
      return `${word("box.member.table.offer.resume.unlimitedDuration")} (${word(
        "box.member.table.offer.item.status.paused.since",
        {
          date: dayjs(this.pause.pausedAt).format("L"),
        },
      )})`
    } else {
      return word("box.member.table.offer.item.status.paused.range", {
        pausedDate: dayjs(this.pause.pausedAt).format("L"),
        resumeDate: dayjs(this.pause.resumeAt).format("L"),
      })
    }
  }

  getCurrentPrice(): PriceObjectEntity | null {
    if (!this.referencePrice?.value) {
      return null
    }

    let price = this.referencePrice.value

    if (this.usedPromoCode) {
      price =
        this.usedPromoCode.type === CouponType.PERCENT
          ? price - (price * this.usedPromoCode.value) / 100
          : price - this.usedPromoCode.value
    }
    if (this.coupon) {
      price =
        this.coupon.type === CouponType.PERCENT ? price - (price * this.coupon.value) / 100 : price - this.coupon.value
    }

    return PriceObjectEntity.fromValue(price, this.referencePrice.currency)
  }
}
